import React, { useState, useMemo, useEffect } from 'react';
import { Student, Teacher, SchoolSettings, AttendanceRecord, AttendanceStatus, HomeroomJournalNote, AgendaEntry } from '../../types.ts';
import * as XLSX from 'xlsx';
import {
  ClassInventoryItem,
  ClassRoomFacility,
  ClassCleanlinessActivity,
  ConditionStatus,
  DEFAULT_INVENTORY_ITEMS,
  DEFAULT_ROOM_FACILITIES,
  DEFAULT_CLEANLINESS_ACTIVITIES
} from './monthly-report/types.ts';
import { generateMonthlyReportPrintHtml } from './monthly-report/MonthlyReportPrint.ts';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Users,
  Printer,
  Download,
  Search,
  Heart,
  FileText,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  Trash2,
  Edit3,
  Check,
  Package,
  Building,
  Sparkles,
  Info,
  Layers,
  HelpCircle,
  ShieldAlert,
  ClipboardList,
  Sparkle
} from 'lucide-react';

interface MonthlyClassReportManagerProps {
  homeroomClass: string;
  students: Student[];
  activeUser: Teacher;
  schoolSettings: SchoolSettings;
  attendanceRecords?: AttendanceRecord[];
  homeroomNotes?: HomeroomJournalNote[];
  onSaveHomeroomNote?: (note: HomeroomJournalNote, method: 'POST' | 'PUT' | 'DELETE') => void | Promise<any>;
  appTheme?: string;
  agendas?: AgendaEntry[];
  agendaAttendanceRecords?: AttendanceRecord[];
}

const INDONESIAN_MONTHS = [
  { value: '01', name: 'Januari' },
  { value: '02', name: 'Februari' },
  { value: '03', name: 'Maret' },
  { value: '04', name: 'April' },
  { value: '05', name: 'Mei' },
  { value: '06', name: 'Juni' },
  { value: '07', name: 'Juli' },
  { value: '08', name: 'Agustus' },
  { value: '09', name: 'September' },
  { value: '10', name: 'Oktober' },
  { value: '11', name: 'November' },
  { value: '12', name: 'Desember' }
];

export const MonthlyClassReportManager: React.FC<MonthlyClassReportManagerProps> = ({
  homeroomClass,
  students = [],
  activeUser,
  schoolSettings,
  attendanceRecords = [],
  homeroomNotes = [],
  onSaveHomeroomNote,
  agendas = [],
  agendaAttendanceRecords = [],
}) => {
  const isSameClass = (c1?: string, c2?: string) => {
    if (!c1 || !c2) return false;
    return c1.toString().trim().toUpperCase() === c2.toString().trim().toUpperCase();
  };

  // Filter agendas for this homeroom class
  const classAgendas = useMemo(() => {
    return (agendas || []).filter((a) => a.class && isSameClass(a.class, homeroomClass));
  }, [agendas, homeroomClass]);

  // Combined effective attendance records
  const effectiveAttendanceRecords = useMemo(() => {
    const map = new Map<string, AttendanceRecord>();

    // 1. Walas records
    (attendanceRecords || []).forEach((r) => {
      if (r.class && !isSameClass(r.class, homeroomClass)) return;
      map.set(`${r.studentId}_${r.date}`, { ...r });
    });

    // 2. Agenda records
    classAgendas.forEach((agenda) => {
      const dateStr = agenda.date;
      if (!dateStr) return;

      const recs = (agendaAttendanceRecords || []).filter(
        (r) => r.agendaId === agenda.id || (r.date === dateStr && r.class && isSameClass(r.class, homeroomClass) && r.agendaId !== 'wk_attendance')
      );

      recs.forEach((ar) => {
        const key = `${ar.studentId}_${dateStr}`;
        const existing = map.get(key);
        const isArNonHadir = ar.status !== AttendanceStatus.Hadir;
        const isExistingDefaultHadir = existing && existing.status === AttendanceStatus.Hadir && !existing.description;

        if (!existing || isExistingDefaultHadir || (isArNonHadir && existing.status === AttendanceStatus.Hadir)) {
          let mergedNote = ar.description || '';
          if (existing?.description && existing.description !== ar.description && !existing.description.includes(ar.description || '')) {
            mergedNote = existing.description + (mergedNote ? ` | ${mergedNote}` : '');
          }

          map.set(key, {
            id: existing?.id || ar.id || `eff_${ar.studentId}_${dateStr}`,
            studentId: ar.studentId,
            date: dateStr,
            status: ar.status,
            description: mergedNote,
            class: homeroomClass,
            teacherId: ar.teacherId || agenda.teacherId || activeUser?.id,
            agendaId: agenda.id,
          });
        }
      });
    });

    return Array.from(map.values());
  }, [attendanceRecords, classAgendas, agendaAttendanceRecords, homeroomClass, activeUser]);
  // Current date
  const today = new Date();
  const currentYearNum = today.getFullYear();
  const currentMonthNum = today.getMonth() + 1;
  const initialMonthStr = currentMonthNum < 10 ? `0${currentMonthNum}` : `${currentMonthNum}`;

  // Month & Year state
  const [selectedMonth, setSelectedMonth] = useState<string>(initialMonthStr);
  const [selectedYear, setSelectedYear] = useState<number>(currentYearNum);

  // Search query & filters
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilterTab, setActiveFilterTab] = useState<'semua' | 'sakit' | 'izin' | 'alpa' | 'terlambat'>('semua');

  // Local storage state for Inventory (Section D)
  const [inventoryItems, setInventoryItems] = useState<ClassInventoryItem[]>(() => {
    try {
      const saved = localStorage.getItem(`smpn1_inventory_${homeroomClass}`);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error(e);
    }
    return DEFAULT_INVENTORY_ITEMS.map((item, idx) => ({
      id: `inv-${idx + 1}`,
      ...item
    }));
  });

  // Local storage state for Room Facilities (Section E)
  const [roomFacilities, setRoomFacilities] = useState<ClassRoomFacility[]>(() => {
    try {
      const saved = localStorage.getItem(`smpn1_room_facilities_${homeroomClass}`);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error(e);
    }
    return DEFAULT_ROOM_FACILITIES.map((item, idx) => ({
      id: `fac-${idx + 1}`,
      ...item
    }));
  });

  // Local storage state for Cleanliness Activities (Section F)
  const [cleanlinessActivities, setCleanlinessActivities] = useState<ClassCleanlinessActivity[]>(() => {
    try {
      const saved = localStorage.getItem(`smpn1_cleanliness_${homeroomClass}`);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error(e);
    }
    return DEFAULT_CLEANLINESS_ACTIVITIES.map((item, idx) => ({
      id: `clean-${idx + 1}`,
      ...item
    }));
  });

  // Save changes to localStorage when inventory, room, or cleanliness updates
  useEffect(() => {
    try {
      localStorage.setItem(`smpn1_inventory_${homeroomClass}`, JSON.stringify(inventoryItems));
    } catch (e) {
      console.error(e);
    }
  }, [inventoryItems, homeroomClass]);

  useEffect(() => {
    try {
      localStorage.setItem(`smpn1_room_facilities_${homeroomClass}`, JSON.stringify(roomFacilities));
    } catch (e) {
      console.error(e);
    }
  }, [roomFacilities, homeroomClass]);

  useEffect(() => {
    try {
      localStorage.setItem(`smpn1_cleanliness_${homeroomClass}`, JSON.stringify(cleanlinessActivities));
    } catch (e) {
      console.error(e);
    }
  }, [cleanlinessActivities, homeroomClass]);

  // Form states for adding items
  const [newInvName, setNewInvName] = useState('');
  const [newInvQty, setNewInvQty] = useState<number>(1);
  const [newInvCondition, setNewInvCondition] = useState<ConditionStatus>('Baik');
  const [newInvNotes, setNewInvNotes] = useState('');
  const [isAddingInvModal, setIsAddingInvModal] = useState(false);

  const [newFacilityName, setNewFacilityName] = useState('');
  const [newFacilityCondition, setNewFacilityCondition] = useState<ConditionStatus>('Baik');
  const [newFacilityNotes, setNewFacilityNotes] = useState('');
  const [isAddingFacilityModal, setIsAddingFacilityModal] = useState(false);

  const [newActivityText, setNewActivityText] = useState('');
  const [newActivitySchedule, setNewActivitySchedule] = useState('');
  const [isAddingActivityModal, setIsAddingActivityModal] = useState(false);

  // Form state for adding Problem Note directly (Section C)
  const [isAddingProblemModal, setIsAddingProblemModal] = useState(false);
  const [problemStudentId, setProblemStudentId] = useState('');
  const [problemCategory, setProblemCategory] = useState<any>('Kedisiplinan');
  const [problemDescription, setProblemDescription] = useState('');
  const [problemAction, setProblemAction] = useState('');
  const [problemStatus, setProblemStatus] = useState<any>('Dalam Proses');

  // Filter students belonging to this homeroom class
  const classStudents = useMemo(() => {
    return students
      .filter((s) => s.class === homeroomClass)
      .sort((a, b) => a.name.localeCompare(b.name, 'id'));
  }, [students, homeroomClass]);

  // Navigation handlers for Month
  const handlePrevMonth = () => {
    const m = parseInt(selectedMonth, 10);
    if (m === 1) {
      setSelectedMonth('12');
      setSelectedYear((prev) => prev - 1);
    } else {
      setSelectedMonth(String(m - 1).padStart(2, '0'));
    }
  };

  const handleNextMonth = () => {
    const m = parseInt(selectedMonth, 10);
    if (m === 12) {
      setSelectedMonth('01');
      setSelectedYear((prev) => prev + 1);
    } else {
      setSelectedMonth(String(m + 1).padStart(2, '0'));
    }
  };

  const handleResetToCurrentMonth = () => {
    setSelectedMonth(initialMonthStr);
    setSelectedYear(currentYearNum);
  };

  const currentMonthObj = useMemo(() => {
    return INDONESIAN_MONTHS.find((m) => m.value === selectedMonth) || INDONESIAN_MONTHS[0];
  }, [selectedMonth]);

  // Semester Boundaries
  const semesterStartDate =
    schoolSettings?.classSemesterDates?.[homeroomClass]?.startDate ||
    schoolSettings?.semesterStartDate ||
    (parseInt(selectedMonth, 10) >= 7 ? `${selectedYear}-07-14` : `${selectedYear}-01-05`);

  const semesterEndDate =
    schoolSettings?.classSemesterDates?.[homeroomClass]?.endDate ||
    schoolSettings?.semesterEndDate ||
    (parseInt(selectedMonth, 10) >= 7 ? `${selectedYear}-12-19` : `${selectedYear}-06-19`);

  const customHolidays = useMemo(() => {
    if (schoolSettings?.classCustomHolidays?.[homeroomClass] && Array.isArray(schoolSettings.classCustomHolidays[homeroomClass])) {
      return schoolSettings.classCustomHolidays[homeroomClass];
    }
    if (schoolSettings?.customHolidays && Array.isArray(schoolSettings.customHolidays)) {
      return schoolSettings.customHolidays;
    }
    return [];
  }, [schoolSettings, homeroomClass]);

  const holidaySet = useMemo(() => new Set(customHolidays.map((h) => h.date)), [customHolidays]);

  // Calculate Effective Working Days
  const workingDaysInMonth = useMemo(() => {
    const yearNum = selectedYear;
    const monthNum = parseInt(selectedMonth, 10);
    const lastDayOfMonth = new Date(yearNum, monthNum, 0).getDate();

    const monthStartStr = `${yearNum}-${selectedMonth}-01`;
    const monthEndStr = `${yearNum}-${selectedMonth}-${String(lastDayOfMonth).padStart(2, '0')}`;

    const effectiveStart = semesterStartDate > monthStartStr ? semesterStartDate : monthStartStr;
    const semesterLimit = semesterEndDate || monthEndStr;
    const todayStrLocal = today.toISOString().split('T')[0];
    const limitBeforeToday = todayStrLocal < semesterLimit ? todayStrLocal : semesterLimit;
    const effectiveEnd = limitBeforeToday < monthEndStr ? limitBeforeToday : monthEndStr;

    if (effectiveStart > effectiveEnd) {
      let count = 0;
      for (let day = 1; day <= lastDayOfMonth; day++) {
        const d = new Date(yearNum, monthNum - 1, day);
        const dayOfWeek = d.getDay();
        const dateStr = `${yearNum}-${selectedMonth}-${String(day).padStart(2, '0')}`;
        if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidaySet.has(dateStr)) {
          count++;
        }
      }
      return Math.max(1, count);
    }

    let count = 0;
    const curr = new Date(effectiveStart);
    const endD = new Date(effectiveEnd);

    while (curr <= endD) {
      const dayOfWeek = curr.getDay();
      const dateStr = curr.toISOString().split('T')[0];
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidaySet.has(dateStr)) {
        count++;
      }
      curr.setDate(curr.getDate() + 1);
    }

    return Math.max(1, count);
  }, [selectedYear, selectedMonth, semesterStartDate, semesterEndDate, holidaySet]);

  // SECTION A: Keadaan Siswa
  const studentDemographics = useMemo(() => {
    const total = classStudents.length;
    const laki = classStudents.filter((s) => s.gender === 'Laki-laki').length;
    const perempuan = classStudents.filter((s) => s.gender === 'Perempuan').length;
    const aktif = classStudents.filter((s) => s.status !== 'Tidak Aktif').length;
    const kipPip = classStudents.filter((s) => s.kipPipStatus && s.kipPipStatus.trim() !== '' && s.kipPipStatus !== '-').length;

    const pctLaki = total > 0 ? Math.round((laki / total) * 100) : 0;
    const pctPerempuan = total > 0 ? Math.round((perempuan / total) * 100) : 0;

    return {
      total,
      laki,
      perempuan,
      aktif,
      kipPip,
      pctLaki,
      pctPerempuan
    };
  }, [classStudents]);

  // SECTION B: Absensi Siswa
  const monthlyAttendanceStats = useMemo(() => {
    const yearNum = selectedYear;
    const monthNum = parseInt(selectedMonth, 10);
    const lastDayOfMonth = new Date(yearNum, monthNum, 0).getDate();

    const monthStartStr = `${yearNum}-${selectedMonth}-01`;
    const monthEndStr = `${yearNum}-${selectedMonth}-${String(lastDayOfMonth).padStart(2, '0')}`;

    const effectiveStart = semesterStartDate > monthStartStr ? semesterStartDate : monthStartStr;
    const semesterLimit = semesterEndDate || monthEndStr;
    const todayStrLocal = new Date().toISOString().split('T')[0];
    const limitBeforeToday = todayStrLocal < semesterLimit ? todayStrLocal : semesterLimit;
    const effectiveEnd = limitBeforeToday < monthEndStr ? limitBeforeToday : monthEndStr;

    const isWeekday = (dateStr: string) => {
      const d = new Date(dateStr);
      return d.getDay() !== 0 && d.getDay() !== 6;
    };

    const classStudentIds = new Set(classStudents.map((s) => s.id));

    const monthlyRecords = effectiveAttendanceRecords.filter((r) => {
      if (!classStudentIds.has(r.studentId)) return false;
      if (!isWeekday(r.date)) return false;
      return r.date >= effectiveStart && r.date <= effectiveEnd;
    });

    const sakitCount = monthlyRecords.filter((r) => r.status === AttendanceStatus.Sakit).length;
    const izinCount = monthlyRecords.filter((r) => r.status === AttendanceStatus.Izin).length;
    const alpaCount = monthlyRecords.filter((r) => r.status === AttendanceStatus.Alpa).length;
    const terlambatCount = monthlyRecords.filter((r) => r.status === AttendanceStatus.Terlambat).length;
    const pulangCount = monthlyRecords.filter((r) => r.status === AttendanceStatus.Pulang).length;

    const totalWorkingDays = workingDaysInMonth;
    const totalStudentDays = totalWorkingDays * (classStudents.length || 1);

    const totalSIA = sakitCount + izinCount + alpaCount;
    const hadirCount = Math.max(0, totalStudentDays - totalSIA);

    const pctSakit = totalStudentDays > 0 ? (sakitCount / totalStudentDays) * 100 : 0;
    const pctIzin = totalStudentDays > 0 ? (izinCount / totalStudentDays) * 100 : 0;
    const pctAlpa = totalStudentDays > 0 ? (alpaCount / totalStudentDays) * 100 : 0;
    const pctTerlambat = totalStudentDays > 0 ? (terlambatCount / totalStudentDays) * 100 : 0;
    const pctPulang = totalStudentDays > 0 ? (pulangCount / totalStudentDays) * 100 : 0;
    const pctHadir = totalStudentDays > 0 ? (hadirCount / totalStudentDays) * 100 : 0;
    const pctSIA = totalStudentDays > 0 ? (totalSIA / totalStudentDays) * 100 : 0;

    const totalStudentCount = classStudents.length || 1;
    const avgSIAPerStudent = Number(((totalSIA / totalStudentCount) / 3).toFixed(2));
    const avgPctSIA = Number((pctSIA / 3).toFixed(2));

    return {
      monthlyRecords,
      totalWorkingDays,
      totalStudentDays,
      sakitCount,
      izinCount,
      alpaCount,
      terlambatCount,
      pulangCount,
      hadirCount,
      totalSIA,
      pctSakit: Number(pctSakit.toFixed(2)),
      pctIzin: Number(pctIzin.toFixed(2)),
      pctAlpa: Number(pctAlpa.toFixed(2)),
      pctTerlambat: Number(pctTerlambat.toFixed(2)),
      pctPulang: Number(pctPulang.toFixed(2)),
      pctHadir: Number(pctHadir.toFixed(2)),
      pctSIA: Number(pctSIA.toFixed(2)),
      avgSIAPerStudent,
      avgPctSIA
    };
  }, [selectedYear, selectedMonth, semesterStartDate, semesterEndDate, effectiveAttendanceRecords, classStudents, workingDaysInMonth]);

  // Breakdown per Student for Attendance Roster
  const studentAttendanceBreakdown = useMemo(() => {
    const { monthlyRecords, totalWorkingDays } = monthlyAttendanceStats;

    return classStudents.map((student, idx) => {
      const recs = monthlyRecords.filter((r) => r.studentId === student.id);

      const S = recs.filter((r) => r.status === AttendanceStatus.Sakit).length;
      const I = recs.filter((r) => r.status === AttendanceStatus.Izin).length;
      const A = recs.filter((r) => r.status === AttendanceStatus.Alpa).length;
      const T = recs.filter((r) => r.status === AttendanceStatus.Terlambat).length;
      const P = recs.filter((r) => r.status === AttendanceStatus.Pulang).length;

      const totalSIA = S + I + A;
      const H = Math.max(0, totalWorkingDays - totalSIA);
      const attendancePercentage = totalWorkingDays > 0 ? Math.round((H / totalWorkingDays) * 100) : 100;

      const detailDates: string[] = [];
      recs.forEach((r) => {
        if (r.status !== AttendanceStatus.Hadir) {
          const formattedD = r.date.substring(8, 10);
          detailDates.push(`${r.status}: ${formattedD}/${selectedMonth}${r.description ? ` (${r.description})` : ''}`);
        }
      });

      return {
        index: idx + 1,
        student,
        H,
        S,
        I,
        A,
        T,
        P,
        totalWorkingDays,
        attendancePercentage,
        detailDates: detailDates.join(', ') || '-'
      };
    });
  }, [classStudents, monthlyAttendanceStats, selectedMonth]);

  // SECTION C: Masalah Siswa (Catatan Bimbingan & Kejadian Peserta Didik)
  // Consolidates late attendance entries per student with detail of dates
  const studentProblems = useMemo(() => {
    const prefixMonth = `${selectedYear}-${selectedMonth}`;

    // Filter raw notes for this class and selected month
    const rawNotes = homeroomNotes.filter((note) => {
      const matchClass = note.homeroomClass === homeroomClass;
      const matchMonth = !note.date || note.date.startsWith(prefixMonth);
      return matchClass && matchMonth;
    });

    // Also get late attendance records for this class in this month
    const classStudentIds = new Set(classStudents.map((s) => s.id));
    const lateAttendanceRecs = attendanceRecords.filter((r) => {
      if (!classStudentIds.has(r.studentId)) return false;
      if (r.status !== AttendanceStatus.Terlambat) return false;
      return r.date && r.date.startsWith(prefixMonth);
    });

    // Helper to check if a note is about late attendance
    const isLateNote = (note: HomeroomJournalNote) => {
      const desc = (note.description || '').toLowerCase();
      const cat = (note.category || '').toLowerCase();
      return desc.includes('terlambat') || cat.includes('terlambat');
    };

    const nonLateNotes: HomeroomJournalNote[] = [];
    const lateMap: Record<
      string,
      {
        studentId: string;
        studentName: string;
        dates: string[];
        actions: string[];
        statuses: string[];
      }
    > = {};

    const addLateInstance = (studentId: string, studentName: string, dateStr: string, action?: string, status?: string) => {
      if (!lateMap[studentId]) {
        lateMap[studentId] = {
          studentId,
          studentName,
          dates: [],
          actions: [],
          statuses: []
        };
      }
      if (dateStr && !lateMap[studentId].dates.includes(dateStr)) {
        lateMap[studentId].dates.push(dateStr);
      }
      if (action && action.trim() && !lateMap[studentId].actions.includes(action.trim())) {
        lateMap[studentId].actions.push(action.trim());
      }
      if (status && !lateMap[studentId].statuses.includes(status)) {
        lateMap[studentId].statuses.push(status);
      }
    };

    // Process raw notes
    rawNotes.forEach((note) => {
      if (isLateNote(note)) {
        const student = classStudents.find(
          (s) => s.id === note.studentId || s.name.toLowerCase() === note.studentName.toLowerCase()
        );
        const studentId = student ? student.id : note.studentId || note.studentName;
        const studentName = student ? student.name : note.studentName;

        let formattedDate = note.date || '';
        if (formattedDate.length >= 10) {
          formattedDate = `${formattedDate.substring(8, 10)}/${selectedMonth}`;
        }
        addLateInstance(studentId, studentName, formattedDate, note.actionTaken, note.status);
      } else {
        nonLateNotes.push(note);
      }
    });

    // Process late attendance records
    lateAttendanceRecs.forEach((rec) => {
      const student = classStudents.find((s) => s.id === rec.studentId);
      if (student) {
        let formattedDate = rec.date.length >= 10 ? `${rec.date.substring(8, 10)}/${selectedMonth}` : rec.date;
        addLateInstance(student.id, student.name, formattedDate, 'Di-jurnal otomatis wali kelas', 'Dalam Proses');
      }
    });

    // Convert consolidated late map into single HomeroomJournalNote entries per student
    const consolidatedLateNotes: HomeroomJournalNote[] = Object.values(lateMap).map((entry) => {
      const sortedDates = entry.dates.sort();
      const count = sortedDates.length;
      const latestStatus = entry.statuses.includes('Perlu Panggilan Ortu')
        ? 'Perlu Panggilan Ortu'
        : entry.statuses.includes('Dalam Proses')
        ? 'Dalam Proses'
        : entry.statuses[0] || 'Dalam Proses';

      const actionSummary =
        entry.actions.filter((a) => a && a !== 'Di-jurnal otomatis wali kelas').join('; ') ||
        'Pembinaan kedisiplinan oleh wali kelas';

      return {
        id: `consolidated-late-${entry.studentId}`,
        date: `${sortedDates.join(', ')} (${count}x)`,
        homeroomClass,
        studentId: entry.studentId,
        studentName: entry.studentName,
        category: 'Kedisiplinan' as any,
        description: `Terlambat hadir sebanyak ${count} kali pada tanggal: ${sortedDates.join(', ')}`,
        actionTaken: actionSummary,
        status: latestStatus as any
      };
    });

    return [...consolidatedLateNotes, ...nonLateNotes];
  }, [homeroomNotes, attendanceRecords, classStudents, homeroomClass, selectedYear, selectedMonth]);

  // Add Inventory Item Handler
  const handleAddInventoryItem = () => {
    if (!newInvName.trim()) {
      alert('Nama barang tidak boleh kosong.');
      return;
    }
    const newItem: ClassInventoryItem = {
      id: `inv-${Date.now()}`,
      name: newInvName.trim(),
      quantity: Math.max(1, newInvQty),
      condition: newInvCondition,
      notes: newInvNotes.trim()
    };
    setInventoryItems((prev) => [...prev, newItem]);
    setNewInvName('');
    setNewInvQty(1);
    setNewInvCondition('Baik');
    setNewInvNotes('');
    setIsAddingInvModal(false);
  };

  const handleUpdateInventoryQuantity = (id: string, quantity: number) => {
    const val = Math.max(0, quantity);
    setInventoryItems((prev) => prev.map((item) => (item.id === id ? { ...item, quantity: val } : item)));
  };

  const handleUpdateInventoryCondition = (id: string, condition: ConditionStatus) => {
    setInventoryItems((prev) => prev.map((item) => (item.id === id ? { ...item, condition } : item)));
  };

  const handleUpdateInventoryNotes = (id: string, notes: string) => {
    setInventoryItems((prev) => prev.map((item) => (item.id === id ? { ...item, notes } : item)));
  };

  const handleDeleteInventoryItem = (id: string) => {
    setInventoryItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Add Facility Handler
  const handleAddFacility = () => {
    if (!newFacilityName.trim()) {
      alert('Nama elemen ruangan tidak boleh kosong.');
      return;
    }
    const newFac: ClassRoomFacility = {
      id: `fac-${Date.now()}`,
      name: newFacilityName.trim(),
      condition: newFacilityCondition,
      notes: newFacilityNotes.trim()
    };
    setRoomFacilities((prev) => [...prev, newFac]);
    setNewFacilityName('');
    setNewFacilityCondition('Baik');
    setNewFacilityNotes('');
    setIsAddingFacilityModal(false);
  };

  const handleUpdateFacilityCondition = (id: string, condition: ConditionStatus) => {
    setRoomFacilities((prev) => prev.map((item) => (item.id === id ? { ...item, condition } : item)));
  };

  const handleUpdateFacilityNotes = (id: string, notes: string) => {
    setRoomFacilities((prev) => prev.map((item) => (item.id === id ? { ...item, notes } : item)));
  };

  const handleDeleteFacility = (id: string) => {
    setRoomFacilities((prev) => prev.filter((item) => item.id !== id));
  };

  // Add Cleanliness Activity Handler
  const handleAddActivity = () => {
    if (!newActivityText.trim()) {
      alert('Deskripsi aktivitas tidak boleh kosong.');
      return;
    }
    const newAct: ClassCleanlinessActivity = {
      id: `clean-${Date.now()}`,
      activity: newActivityText.trim(),
      scheduleOrPerson: newActivitySchedule.trim() || 'Rutin / Regu Piket'
    };
    setCleanlinessActivities((prev) => [...prev, newAct]);
    setNewActivityText('');
    setNewActivitySchedule('');
    setIsAddingActivityModal(false);
  };

  const handleDeleteActivity = (id: string) => {
    setCleanlinessActivities((prev) => prev.filter((item) => item.id !== id));
  };

  // Add Problem Note Handler (Section C)
  const handleAddProblemNote = async () => {
    if (!problemStudentId) {
      alert('Silakan pilih peserta didik.');
      return;
    }
    if (!problemDescription.trim()) {
      alert('Isi deskripsi masalah / kejadian.');
      return;
    }
    const targetStudent = classStudents.find((s) => s.id === problemStudentId);
    const newNote: HomeroomJournalNote = {
      id: `note-${Date.now()}`,
      date: `${selectedYear}-${selectedMonth}-${String(today.getDate()).padStart(2, '0')}`,
      homeroomClass,
      studentId: problemStudentId,
      studentName: targetStudent?.name || 'Siswa',
      category: problemCategory,
      description: problemDescription.trim(),
      actionTaken: problemAction.trim() || 'Pembinaan awal oleh wali kelas',
      status: problemStatus,
      createdAt: new Date().toISOString()
    };

    if (onSaveHomeroomNote) {
      await onSaveHomeroomNote(newNote, 'POST');
    }
    setIsAddingProblemModal(false);
    setProblemStudentId('');
    setProblemDescription('');
    setProblemAction('');
  };

  // Print Report Handler
  const handlePrintFullReport = () => {
    const printWin = window.open('', '_blank');
    if (!printWin) return;

    const htmlContent = generateMonthlyReportPrintHtml({
      homeroomClass,
      selectedMonthName: currentMonthObj.name,
      selectedYear,
      schoolSettings,
      activeUser,
      studentDemographics,
      attendanceStats: monthlyAttendanceStats,
      studentAttendanceRows: studentAttendanceBreakdown,
      studentProblems,
      inventoryItems,
      roomFacilities,
      cleanlinessActivities
    });

    printWin.document.open();
    printWin.document.write(htmlContent);
    printWin.document.close();
  };

  // Export Excel Handler
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Rekap Utama Laporan Bulanan (A-F)
    const summaryData = [
      { Kategori: 'LAPORAN BULANAN KELAS', Value: homeroomClass },
      { Kategori: 'Periode Bulan', Value: `${currentMonthObj.name} ${selectedYear}` },
      { Kategori: 'Wali Kelas', Value: activeUser?.name || '-' },
      { Kategori: '', Value: '' },
      { Kategori: '--- A. KEADAAN SISWA ---', Value: '' },
      { Kategori: 'Jumlah Laki-laki (L)', Value: `${studentDemographics.laki} Orang (${studentDemographics.pctLaki}%)` },
      { Kategori: 'Jumlah Perempuan (P)', Value: `${studentDemographics.perempuan} Orang (${studentDemographics.pctPerempuan}%)` },
      { Kategori: 'Jumlah Seluruh Siswa', Value: `${studentDemographics.total} Orang` },
      { Kategori: '', Value: '' },
      { Kategori: '--- B. ABSENSI SISWA ---', Value: '' },
      { Kategori: 'Jumlah Sakit (S)', Value: `${monthlyAttendanceStats.sakitCount} Hari (${monthlyAttendanceStats.pctSakit}%)` },
      { Kategori: 'Jumlah Izin (I)', Value: `${monthlyAttendanceStats.izinCount} Hari (${monthlyAttendanceStats.pctIzin}%)` },
      { Kategori: 'Jumlah Alpa (A)', Value: `${monthlyAttendanceStats.alpaCount} Hari (${monthlyAttendanceStats.pctAlpa}%)` },
      { Kategori: 'Total S+I+A', Value: `${monthlyAttendanceStats.totalSIA} Hari (${monthlyAttendanceStats.pctSIA}%)` },
      { Kategori: 'Rata-rata S+I+A per Siswa', Value: `${monthlyAttendanceStats.avgSIAPerStudent} Hari/Siswa` },
      { Kategori: 'Rata-rata % S+I+A', Value: `${monthlyAttendanceStats.avgPctSIA}%` },
      { Kategori: 'Tingkat Kehadiran Kelas (H)', Value: `${monthlyAttendanceStats.pctHadir}%` }
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Ringkasan Laporan');

    // Sheet 2: Inventory (D)
    const invData = inventoryItems.map((item, idx) => ({
      No: idx + 1,
      'Nama Barang': item.name,
      Jumlah: item.quantity,
      Kondisi: item.condition,
      Keterangan: item.notes
    }));
    const wsInv = XLSX.utils.json_to_sheet(invData);
    XLSX.utils.book_append_sheet(wb, wsInv, 'Inventaris Kelas');

    // Sheet 3: Room Facilities (E)
    const facData = roomFacilities.map((fac, idx) => ({
      No: idx + 1,
      'Nama Sarana Ruangan': fac.name,
      Kondisi: fac.condition,
      Keterangan: fac.notes
    }));
    const wsFac = XLSX.utils.json_to_sheet(facData);
    XLSX.utils.book_append_sheet(wb, wsFac, 'Keadaan Ruangan');

    // Sheet 4: Cleanliness Activities (F)
    const cleanData = cleanlinessActivities.map((act, idx) => ({
      No: idx + 1,
      'Aktivitas Memelihara Kebersihan': act.activity,
      'Keterangan / Jadwal': act.scheduleOrPerson
    }));
    const wsClean = XLSX.utils.json_to_sheet(cleanData);
    XLSX.utils.book_append_sheet(wb, wsClean, 'Kebersihan Kelas');

    XLSX.writeFile(wb, `Laporan_Bulanan_Kelas_${homeroomClass}_${currentMonthObj.name}_${selectedYear}.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* Header & Month Navigator */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-700/80 shadow-sm">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20">
              <ClipboardList size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg sm:text-xl font-black text-slate-800 dark:text-white">
                  Laporan Bulanan Kelas {homeroomClass}
                </h2>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-indigo-100 text-indigo-800 dark:bg-indigo-950/70 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                  Lengkap (Bagian A - F)
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Keadaan siswa, absensi, catatan masalah, inventaris, keadaan ruangan, & kebersihan kelas
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full lg:w-auto">
            <button
              onClick={handlePrintFullReport}
              className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-black dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs sm:text-sm font-bold shadow-sm cursor-pointer transition-all"
            >
              <Printer size={16} />
              <span>Cetak Laporan Bulanan</span>
            </button>
            <button
              onClick={handleExportExcel}
              className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-bold shadow-sm cursor-pointer transition-all"
            >
              <Download size={16} />
              <span>Ekspor Excel</span>
            </button>
          </div>
        </div>

        {/* Month Navigator Bar (Next & Back Buttons) */}
        <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-700/60 flex flex-wrap items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl">
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevMonth}
              className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 text-xs sm:text-sm font-bold shadow-2xs cursor-pointer transition-all"
              title="Bulan Sebelumnya"
            >
              <ChevronLeft size={16} />
              <span className="hidden sm:inline">Bulan Sebelumnya</span>
            </button>

            <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-2xs">
              <Calendar size={16} className="text-indigo-500" />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-transparent font-extrabold text-xs sm:text-sm text-slate-800 dark:text-white outline-none cursor-pointer"
              >
                {INDONESIAN_MONTHS.map((m) => (
                  <option key={m.value} value={m.value} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white">
                    {m.name}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="bg-transparent font-extrabold text-xs sm:text-sm text-slate-800 dark:text-white outline-none cursor-pointer"
              >
                {[currentYearNum - 1, currentYearNum, currentYearNum + 1].map((y) => (
                  <option key={y} value={y} className="bg-white dark:bg-slate-800 text-slate-800 dark:text-white">
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleNextMonth}
              className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-600 text-xs sm:text-sm font-bold shadow-2xs cursor-pointer transition-all"
              title="Bulan Berikutnya"
            >
              <span className="hidden sm:inline">Bulan Berikutnya</span>
              <ChevronRight size={16} />
            </button>

            <button
              onClick={handleResetToCurrentMonth}
              className="px-3 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-xs font-bold cursor-pointer transition-all"
            >
              Bulan Ini
            </button>
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300 font-semibold">
            <span>
              Wali Kelas: <strong className="text-slate-900 dark:text-white">{activeUser?.name || '-'}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* SECTION A: KEADAAN SISWA */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-700/80 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center font-black text-sm">
              A
            </div>
            <div>
              <h3 className="font-extrabold text-slate-800 dark:text-white text-base">A. Keadaan Siswa</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Komposisi peserta didik laki-laki, perempuan, dan jumlah seluruhnya</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-950/40 dark:to-sky-950/30 border border-blue-200 dark:border-blue-900/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase">Siswa Laki-laki (L)</span>
              <span className="px-2 py-0.5 rounded-full text-xs font-black bg-blue-600 text-white">{studentDemographics.pctLaki}%</span>
            </div>
            <div className="mt-2 text-3xl font-black text-blue-900 dark:text-blue-200">{studentDemographics.laki} <span className="text-xs font-normal text-blue-600">Siswa</span></div>
          </div>

          <div className="bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-950/40 dark:to-rose-950/30 border border-pink-200 dark:border-pink-900/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-pink-700 dark:text-pink-300 uppercase">Siswa Perempuan (P)</span>
              <span className="px-2 py-0.5 rounded-full text-xs font-black bg-pink-600 text-white">{studentDemographics.pctPerempuan}%</span>
            </div>
            <div className="mt-2 text-3xl font-black text-pink-900 dark:text-pink-200">{studentDemographics.perempuan} <span className="text-xs font-normal text-pink-600">Siswa</span></div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300 uppercase">Jumlah Seluruh Siswa</span>
              <span className="px-2 py-0.5 rounded-full text-xs font-black bg-emerald-600 text-white">100%</span>
            </div>
            <div className="mt-2 text-3xl font-black text-emerald-900 dark:text-emerald-200">{studentDemographics.total} <span className="text-xs font-normal text-emerald-600">Siswa Terdaftar</span></div>
          </div>
        </div>
      </div>

      {/* SECTION B: ABSENSI SISWA */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-700/80 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-purple-100 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 flex items-center justify-center font-black text-sm">
              B
            </div>
            <div>
              <h3 className="font-extrabold text-slate-800 dark:text-white text-base">B. Absensi Siswa</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Rekapitulasi Sakit, Izin, Alpa, Total Ketidakhadiran, Rata-rata, & Kehadiran Kelas</p>
            </div>
          </div>
          <span className="text-xs font-bold px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full">
            Kehadiran Kelas: {monthlyAttendanceStats.pctHadir}%
          </span>
        </div>

        {/* Highlighted Metric Grid for Section B */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Sakit */}
          <div className="bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-900/50 rounded-xl p-3.5">
            <div className="text-[11px] font-bold text-sky-800 dark:text-sky-300 uppercase">Sakit (S)</div>
            <div className="mt-1 text-2xl font-black text-sky-900 dark:text-sky-100">{monthlyAttendanceStats.sakitCount} <span className="text-xs font-normal">Hari</span></div>
            <div className="text-xs font-extrabold text-sky-600 dark:text-sky-400 mt-0.5">{monthlyAttendanceStats.pctSakit}%</div>
          </div>

          {/* Izin */}
          <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-xl p-3.5">
            <div className="text-[11px] font-bold text-amber-800 dark:text-amber-300 uppercase">Izin (I)</div>
            <div className="mt-1 text-2xl font-black text-amber-900 dark:text-amber-100">{monthlyAttendanceStats.izinCount} <span className="text-xs font-normal">Hari</span></div>
            <div className="text-xs font-extrabold text-amber-600 dark:text-amber-400 mt-0.5">{monthlyAttendanceStats.pctIzin}%</div>
          </div>

          {/* Alpa */}
          <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 rounded-xl p-3.5">
            <div className="text-[11px] font-bold text-rose-800 dark:text-rose-300 uppercase">Alpa (A)</div>
            <div className="mt-1 text-2xl font-black text-rose-900 dark:text-rose-100">{monthlyAttendanceStats.alpaCount} <span className="text-xs font-normal">Hari</span></div>
            <div className="text-xs font-extrabold text-rose-600 dark:text-rose-400 mt-0.5">{monthlyAttendanceStats.pctAlpa}%</div>
          </div>

          {/* Total S+I+A */}
          <div className="bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-900/50 rounded-xl p-3.5">
            <div className="text-[11px] font-bold text-purple-800 dark:text-purple-300 uppercase">Total S + I + A</div>
            <div className="mt-1 text-2xl font-black text-purple-900 dark:text-purple-100">{monthlyAttendanceStats.totalSIA} <span className="text-xs font-normal">Hari</span></div>
            <div className="text-xs font-extrabold text-purple-600 dark:text-purple-400 mt-0.5">{monthlyAttendanceStats.pctSIA}%</div>
          </div>

          {/* Rata-rata S+I+A per Siswa */}
          <div className="bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900/50 rounded-xl p-3.5">
            <div className="text-[11px] font-bold text-indigo-800 dark:text-indigo-300 uppercase">Rata2 S+I+A</div>
            <div className="mt-1 text-2xl font-black text-indigo-900 dark:text-indigo-100">{monthlyAttendanceStats.avgSIAPerStudent} <span className="text-xs font-normal">Hari/Siswa</span></div>
            <div className="text-xs font-extrabold text-indigo-600 dark:text-indigo-400 mt-0.5">Rata2: {monthlyAttendanceStats.avgPctSIA}%</div>
          </div>

          {/* Hadir */}
          <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/50 rounded-xl p-3.5">
            <div className="text-[11px] font-bold text-emerald-800 dark:text-emerald-300 uppercase">Hadir (H)</div>
            <div className="mt-1 text-2xl font-black text-emerald-900 dark:text-emerald-100">{monthlyAttendanceStats.hadirCount} <span className="text-xs font-normal">Hari</span></div>
            <div className="text-xs font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5">Kehadiran: {monthlyAttendanceStats.pctHadir}%</div>
          </div>
        </div>
      </div>

      {/* SECTION C: MASALAH SISWA */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-700/80 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center font-black text-sm">
              C
            </div>
            <div>
              <h3 className="font-extrabold text-slate-800 dark:text-white text-base">C. Masalah Siswa</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Ditarik dari Catatan Bimbingan & Kejadian Peserta Didik bulan {currentMonthObj.name} {selectedYear}</p>
            </div>
          </div>

          <button
            onClick={() => setIsAddingProblemModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold cursor-pointer transition-all"
          >
            <Plus size={14} />
            <span>Tambah Catatan Kejadian</span>
          </button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold uppercase text-[10.5px]">
              <tr>
                <th className="py-2.5 px-3 text-center w-10">No</th>
                <th className="py-2.5 px-3 w-28">Tanggal</th>
                <th className="py-2.5 px-3">Nama Siswa</th>
                <th className="py-2.5 px-3 w-28">Kategori</th>
                <th className="py-2.5 px-3">Deskripsi Kejadian / Masalah</th>
                <th className="py-2.5 px-3">Tindak Lanjut</th>
                <th className="py-2.5 px-3 text-center w-28">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60 bg-white dark:bg-slate-800">
              {studentProblems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-slate-400 font-medium">
                    Nihil (Tidak ada catatan masalah peserta didik pada bulan {currentMonthObj.name} {selectedYear}).
                  </td>
                </tr>
              ) : (
                studentProblems.map((p, idx) => (
                  <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                    <td className="py-2 px-3 text-center text-slate-500">{idx + 1}</td>
                    <td className="py-2 px-3 font-semibold text-slate-700 dark:text-slate-300">{p.date || '-'}</td>
                    <td className="py-2 px-3 font-bold text-slate-900 dark:text-white">{p.studentName}</td>
                    <td className="py-2 px-3">
                      <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-[10px] font-bold">
                        {p.category}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-slate-800 dark:text-slate-200">{p.description}</td>
                    <td className="py-2 px-3 text-slate-700 dark:text-slate-300">{p.actionTaken || '-'}</td>
                    <td className="py-2 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                        p.status === 'Selesai'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {p.status || 'Dalam Proses'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* SECTION D: BARANG INVENTARIS KELAS */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-700/80 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-indigo-100 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-black text-sm">
              D
            </div>
            <div>
              <h3 className="font-extrabold text-slate-800 dark:text-white text-base">D. Keadaan Barang Inventaris Kelas</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Garuda, foto Presiden/Wapres, meja, kursi, papan tulis, lemari, kebersihan, dll.</p>
            </div>
          </div>

          <button
            onClick={() => setIsAddingInvModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold cursor-pointer transition-all"
          >
            <Plus size={14} />
            <span>Tambahkan Barang</span>
          </button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold uppercase text-[10.5px]">
              <tr>
                <th className="py-2.5 px-3 text-center w-10">No</th>
                <th className="py-2.5 px-3">Nama Barang Inventaris</th>
                <th className="py-2.5 px-3 text-center w-20">Jumlah</th>
                <th className="py-2.5 px-3 text-center w-48">Kondisi (Checklist)</th>
                <th className="py-2.5 px-3">Keterangan</th>
                <th className="py-2.5 px-3 text-center w-12">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60 bg-white dark:bg-slate-800">
              {inventoryItems.map((item, idx) => (
                <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  <td className="py-2 px-3 text-center text-slate-500">{idx + 1}</td>
                  <td className="py-2 px-3 font-bold text-slate-800 dark:text-white">{item.name}</td>
                  <td className="py-2 px-3 text-center">
                    <input
                      type="number"
                      min={0}
                      value={item.quantity}
                      onChange={(e) => handleUpdateInventoryQuantity(item.id, parseInt(e.target.value, 10) || 0)}
                      className="w-16 px-2 py-1 text-center font-black bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-900 dark:text-white outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-center gap-1">
                      {(['Baik', 'Rusak Ringan', 'Rusak Berat'] as ConditionStatus[]).map((cond) => (
                        <button
                          key={cond}
                          onClick={() => handleUpdateInventoryCondition(item.id, cond)}
                          className={`px-2 py-0.5 rounded text-[10px] font-extrabold cursor-pointer transition-all ${
                            item.condition === cond
                              ? cond === 'Baik'
                                ? 'bg-emerald-600 text-white shadow-2xs'
                                : cond === 'Rusak Ringan'
                                ? 'bg-amber-500 text-white shadow-2xs'
                                : 'bg-rose-600 text-white shadow-2xs'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          {cond}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="text"
                      value={item.notes}
                      onChange={(e) => handleUpdateInventoryNotes(item.id, e.target.value)}
                      className="w-full px-2 py-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-800 dark:text-white outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="Tambahkan catatan kondisi..."
                    />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <button
                      onClick={() => handleDeleteInventoryItem(item.id)}
                      className="text-slate-400 hover:text-rose-600 cursor-pointer p-1"
                      title="Hapus Barang"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* SECTION E: KEADAAN RUANG KELAS */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-700/80 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-teal-100 dark:bg-teal-950/60 text-teal-600 dark:text-teal-400 flex items-center justify-center font-black text-sm">
              E
            </div>
            <div>
              <h3 className="font-extrabold text-slate-800 dark:text-white text-base">E. Keadaan Ruang Kelas</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Lantai, langit-langit, atap/genting, pintu, jendela, teras, sarana listrik, dll.</p>
            </div>
          </div>

          <button
            onClick={() => setIsAddingFacilityModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold cursor-pointer transition-all"
          >
            <Plus size={14} />
            <span>Tambahkan Elemen Ruangan</span>
          </button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold uppercase text-[10.5px]">
              <tr>
                <th className="py-2.5 px-3 text-center w-10">No</th>
                <th className="py-2.5 px-3">Nama Elemen Sarana Ruangan</th>
                <th className="py-2.5 px-3 text-center w-48">Kondisi</th>
                <th className="py-2.5 px-3">Keterangan</th>
                <th className="py-2.5 px-3 text-center w-12">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60 bg-white dark:bg-slate-800">
              {roomFacilities.map((fac, idx) => (
                <tr key={fac.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  <td className="py-2 px-3 text-center text-slate-500">{idx + 1}</td>
                  <td className="py-2 px-3 font-bold text-slate-800 dark:text-white">{fac.name}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-center gap-1">
                      {(['Baik', 'Rusak Ringan', 'Rusak Berat'] as ConditionStatus[]).map((cond) => (
                        <button
                          key={cond}
                          onClick={() => handleUpdateFacilityCondition(fac.id, cond)}
                          className={`px-2 py-0.5 rounded text-[10px] font-extrabold cursor-pointer transition-all ${
                            fac.condition === cond
                              ? cond === 'Baik'
                                ? 'bg-emerald-600 text-white shadow-2xs'
                                : cond === 'Rusak Ringan'
                                ? 'bg-amber-500 text-white shadow-2xs'
                                : 'bg-rose-600 text-white shadow-2xs'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          {cond}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="text"
                      value={fac.notes}
                      onChange={(e) => handleUpdateFacilityNotes(fac.id, e.target.value)}
                      className="w-full px-2 py-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-800 dark:text-white outline-none focus:ring-1 focus:ring-teal-500"
                      placeholder="Tambahkan catatan kondisi..."
                    />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <button
                      onClick={() => handleDeleteFacility(fac.id)}
                      className="text-slate-400 hover:text-rose-600 cursor-pointer p-1"
                      title="Hapus Elemen"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* SECTION F: KEBERSIHAN DAN KEINDAHAN KELAS */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-700/80 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-black text-sm">
              F
            </div>
            <div>
              <h3 className="font-extrabold text-slate-800 dark:text-white text-base">F. Kebersihan dan Keindahan Kelas</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Aktivitas yang dilakukan untuk memelihara kebersihan dan keindahan ruangan kelas</p>
            </div>
          </div>

          <button
            onClick={() => setIsAddingActivityModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold cursor-pointer transition-all"
          >
            <Plus size={14} />
            <span>Tambahkan Aktivitas</span>
          </button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold uppercase text-[10.5px]">
              <tr>
                <th className="py-2.5 px-3 text-center w-10">No.</th>
                <th className="py-2.5 px-3">Aktivitas Yang Dilakukan Untuk Memelihara Kebersihan dan Keindahan Ruangan Kelas</th>
                <th className="py-2.5 px-3 w-64">Keterangan / Jadwal / Penanggung Jawab</th>
                <th className="py-2.5 px-3 text-center w-12">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60 bg-white dark:bg-slate-800">
              {cleanlinessActivities.map((act, idx) => (
                <tr key={act.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  <td className="py-2.5 px-3 text-center font-bold text-slate-500">{idx + 1}</td>
                  <td className="py-2.5 px-3 font-semibold text-slate-800 dark:text-white">{act.activity}</td>
                  <td className="py-2.5 px-3 text-slate-600 dark:text-slate-300 font-medium">{act.scheduleOrPerson}</td>
                  <td className="py-2.5 px-3 text-center">
                    <button
                      onClick={() => handleDeleteActivity(act.id)}
                      className="text-slate-400 hover:text-rose-600 cursor-pointer p-1"
                      title="Hapus Aktivitas"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL: Tambah Barang Inventaris */}
      {isAddingInvModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 dark:border-slate-700 space-y-4">
            <h3 className="font-extrabold text-base text-slate-800 dark:text-white">Form Tambahkan Barang Inventaris</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Nama Barang</label>
                <input
                  type="text"
                  value={newInvName}
                  onChange={(e) => setNewInvName(e.target.value)}
                  placeholder="e.g. Proyektor LCD Kelas"
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Jumlah</label>
                  <input
                    type="number"
                    min={1}
                    value={newInvQty}
                    onChange={(e) => setNewInvQty(parseInt(e.target.value, 10) || 1)}
                    className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Kondisi</label>
                  <select
                    value={newInvCondition}
                    onChange={(e) => setNewInvCondition(e.target.value as ConditionStatus)}
                    className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                  >
                    <option value="Baik">Baik</option>
                    <option value="Rusak Ringan">Rusak Ringan</option>
                    <option value="Rusak Berat">Rusak Berat</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Keterangan</label>
                <input
                  type="text"
                  value={newInvNotes}
                  onChange={(e) => setNewInvNotes(e.target.value)}
                  placeholder="Catatan kondisi/posisi barang..."
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setIsAddingInvModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Batal
              </button>
              <button
                onClick={handleAddInventoryItem}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Simpan Barang
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Tambah Elemen Ruangan */}
      {isAddingFacilityModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 dark:border-slate-700 space-y-4">
            <h3 className="font-extrabold text-base text-slate-800 dark:text-white">Form Tambahkan Elemen Ruangan</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Nama Sarana / Elemen Ruang</label>
                <input
                  type="text"
                  value={newFacilityName}
                  onChange={(e) => setNewFacilityName(e.target.value)}
                  placeholder="e.g. Pojok Baca / Rak Buku"
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Kondisi</label>
                <select
                  value={newFacilityCondition}
                  onChange={(e) => setNewFacilityCondition(e.target.value as ConditionStatus)}
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                >
                  <option value="Baik">Baik</option>
                  <option value="Rusak Ringan">Rusak Ringan</option>
                  <option value="Rusak Berat">Rusak Berat</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Keterangan</label>
                <input
                  type="text"
                  value={newFacilityNotes}
                  onChange={(e) => setNewFacilityNotes(e.target.value)}
                  placeholder="Catatan kebersihan/kondisi..."
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setIsAddingFacilityModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Batal
              </button>
              <button
                onClick={handleAddFacility}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-teal-600 text-white hover:bg-teal-700"
              >
                Simpan Elemen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Tambah Aktivitas Kebersihan */}
      {isAddingActivityModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 dark:border-slate-700 space-y-4">
            <h3 className="font-extrabold text-base text-slate-800 dark:text-white">Form Tambahkan Aktivitas Kebersihan</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Aktivitas Pemeliharaan Kebersihan</label>
                <textarea
                  rows={3}
                  value={newActivityText}
                  onChange={(e) => setNewActivityText(e.target.value)}
                  placeholder="e.g. Pembersihan sawang dan debu pada kipas angin..."
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Keterangan / Jadwal / Penanggung Jawab</label>
                <input
                  type="text"
                  value={newActivitySchedule}
                  onChange={(e) => setNewActivitySchedule(e.target.value)}
                  placeholder="e.g. Setiap Sabtu / Seluruh Siswa"
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setIsAddingActivityModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Batal
              </button>
              <button
                onClick={handleAddActivity}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Simpan Aktivitas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Tambah Catatan Masalah Siswa (Section C) */}
      {isAddingProblemModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-slate-200 dark:border-slate-700 space-y-4">
            <h3 className="font-extrabold text-base text-slate-800 dark:text-white">Tambah Catatan Kejadian / Masalah Peserta Didik</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Pilih Peserta Didik</label>
                <select
                  value={problemStudentId}
                  onChange={(e) => setProblemStudentId(e.target.value)}
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none font-semibold"
                >
                  <option value="">-- Pilih Siswa Kelas {homeroomClass} --</option>
                  {classStudents.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.gender === 'Laki-laki' ? 'L' : 'P'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Kategori</label>
                  <select
                    value={problemCategory}
                    onChange={(e) => setProblemCategory(e.target.value as any)}
                    className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none font-semibold"
                  >
                    <option value="Kedisiplinan">Kedisiplinan</option>
                    <option value="Akademik">Akademik</option>
                    <option value="Sosial/Sikap">Sosial / Sikap</option>
                    <option value="Kesehatan">Kesehatan</option>
                    <option value="Karakter">Karakter</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Status Tindak Lanjut</label>
                  <select
                    value={problemStatus}
                    onChange={(e) => setProblemStatus(e.target.value as any)}
                    className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none font-semibold"
                  >
                    <option value="Dalam Proses">Dalam Proses</option>
                    <option value="Selesai">Selesai</option>
                    <option value="Perlu Panggilan Ortu">Perlu Panggilan Ortu</option>
                    <option value="Perlu Konseling BK">Perlu Konseling BK</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Deskripsi Masalah / Kejadian</label>
                <textarea
                  rows={3}
                  value={problemDescription}
                  onChange={(e) => setProblemDescription(e.target.value)}
                  placeholder="Deskripsi ringkas masalah atau kejadian..."
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Tindak Lanjut / Penanganan</label>
                <input
                  type="text"
                  value={problemAction}
                  onChange={(e) => setProblemAction(e.target.value)}
                  placeholder="e.g. Pembimbingan wali kelas & konfirmasi ke orang tua"
                  className="w-full mt-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => setIsAddingProblemModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Batal
              </button>
              <button
                onClick={handleAddProblemNote}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-amber-500 text-white hover:bg-amber-600"
              >
                Simpan Catatan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
